import React, { useEffect, useState } from "react";
import {
	Button,
	Form,
	FormGroup,
	Input,
	InputGroup,
	InputGroupAddon,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Table,
	Row,
	Col
} from "reactstrap";
import { FileText, Loader, Plus, Upload, X } from "react-feather";
import { Dispatch } from "redux";
import reportService from "../../../services/reportService";
import { connect } from "react-redux";
import { paginateReports } from "../../../redux/actions/reports/reports";
import { IApplicationState } from "../../../redux/reducers";
import { IReport } from "../../../interfaces/report";
import { IPagination } from "../../../interfaces/pagination";
import { SAMPLE_REPORTS } from "../reports/sampleReports";
import ReportRow from "../../analysis/components/reportTableRow";
import { REPORTS_ALLOWED_FILE_EXTENSIONS } from "../../../utils/constants";
import { error } from "../../../utils/notification";
import { SelectField } from "../../../components/form/Select";

interface IProps {
	reports: IReport[];
	requests: IReport[];
	pagination: IPagination;
	user_email: string | undefined;
	role?: string;
	paginateReports: (page: number) => void;
	getAllReportRequest: () => Promise<any>;
	getAllReports: () => Promise<any>;
	addReport: (reportType: string, reportFile: File, reportDescription: string) => Promise<any>;
	addReportRequest: (reportType: string, reportDescription: string) => Promise<any>;
}

const FILE_SUPPORTED = [
	"pdf",
	"jpg",
	"jpeg",
	"png",
	"csv",
	"vnd.ms-excel",
	"vnd.openxmlformats-officedocument.spreadsheetml.sheet"
];

const Labels: React.FC<IProps> = ({
	reports,
	role,
	user_email,
	paginateReports,
	getAllReports,
	addReport
}) => {
	const [modal, setModal] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [reportFile, setReportFile] = useState<File>();
	const [reportType, setReportType] = useState<{ label: string; value: string } | null>(null);
	const [reportDescription, setReportDescription] = useState<string>("");
	const [adding, setAdding] = useState<boolean>(false);
	const [isFormatValid, setIsFormatValid] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string>(
		"File format supported : pdf, excel, csv, jpg and png."
	);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [activeFilter, setActiveFilter] = useState<string>("All");

	const DOCUMENT_TYPES = [
		{ label: "Nutritional", value: "Nutritional" },
		{ label: "Sustainability", value: "Sustainability" },
		{ label: "Regulatory", value: "Regulatory" },
		{ label: "Others", value: "Others" }
	];

	const FILTER_TABS = ["All", "PDF", "Photo", "Spreadsheet"];

	function toggleAddForm() {
		setIsFormatValid(false);
		setErrorMessage("File format supported : pdf, excel, csv, jpg and png.");
		setModal(!modal);
		setReportType(null);
		setReportDescription("");
		setReportFile(undefined);
	}

	function selectReportFile(event: any) {
		if (event.target.files[0]) {
			const file = event.target.files[0];
			const type = file.type.split("/")[1];
			// Handle spreadsheet types specifically
			const isSpreadsheet = file.type.includes("sheet") || file.type.includes("excel") || type === "csv";
			
			if (!FILE_SUPPORTED.includes(type) && !isSpreadsheet) {
				setIsFormatValid(false);
				setErrorMessage("File type not supported.");
			} else {
				setIsFormatValid(true);
				setErrorMessage("File type supported.");
			}
			setReportFile(file);
		} else {
			setErrorMessage("No file chosen.");
		}
	}

	async function onPageChanged(page: number) {
		setLoading(true);
		await paginateReports(page);
		getAllReports().finally(() => {
			setLoading(false);
		});
	}

	function createNewReport() {
		if (!reportType?.value || (reportDescription?.length || 0) < 50 || reportFile === undefined) {
			error("Please complete all fields correctly. Description must be at least 50 characters.");
			return;
		} else {
			setAdding(true);
			addReport(reportType.value, reportFile, reportDescription).finally(() => {
				getAllReports().finally(() => {
					setAdding(false);
					setModal(false);
					setErrorMessage("File format supported : pdf, excel, csv, jpg and png.");
					setIsFormatValid(false);
				});
			});
		}
	}

	useEffect(() => {
		setLoading(true);
		Promise.all([getAllReports()]).finally(() => {
			setLoading(false);
		});
	}, []);

	const allAvailableReports = (user_email === "opcito+compuser@journeyfoods.com" ? SAMPLE_REPORTS : []).concat(reports);
	
	const filteredReports = allAvailableReports.filter((report: any) => {
		const searchTermLower = searchTerm.toLowerCase();
		const matchesSearch =
			report.report_type?.toLowerCase().includes(searchTermLower) ||
			report.description?.toLowerCase().includes(searchTermLower) ||
			report.requested_by?.name?.toLowerCase().includes(searchTermLower);

		if (!matchesSearch) return false;

		if (activeFilter === "All") return true;
		const extension = report.report_uri?.split("?")[0].split(".").pop()?.toLowerCase();
		if (activeFilter === "PDF") return extension === "pdf";
		if (activeFilter === "Photo") return ["jpg", "jpeg", "png"].includes(extension || "");
		if (activeFilter === "Spreadsheet") return ["csv", "xlsx", "xls"].includes(extension || "");
		return true;
	});

	const isDescriptionValid = (reportDescription?.length || 0) >= 50;

	return (
		<div className="page-content container-fluid reports">
			<div className="all-reports mt-4 pt-2">
				<div className="page-header d-flex justify-content-between align-items-center mb-4">
					<h4 className="font-weight-semibold m-0">All Documents</h4>
					{(role === "JF_ADMIN" ||
						role === "COMPANY_ADMIN" ||
						role === "JF_STAFF" ||
						role === "COMPANY_USER") && (
						<Button color="primary" className="px-4" onClick={() => setModal(true)}>
							<Upload size={18} className="mr-2" />
							Upload Document
						</Button>
					)}
				</div>

				<div className="documents-search-bar-container mb-4">
					<Row className="align-items-center">
						<Col md={6}>
							<InputGroup className="search-bar">
								<Input
									placeholder="Search documents..."
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									className="border-right-0"
								/>
								<InputGroupAddon addonType="append">
									<div className="input-group-text bg-white border-left-0">
										<FileText size={18} color="#6c757d" />
									</div>
								</InputGroupAddon>
							</InputGroup>
						</Col>
						<Col md={6} className="text-right d-flex justify-content-end align-items-center">
							<div className="filter-tabs">
								{FILTER_TABS.map((tab) => (
									<span
										key={tab}
										className={`filter-tab ${activeFilter === tab ? "active" : ""}`}
										onClick={() => setActiveFilter(tab)}
									>
										{tab}
									</span>
								))}
							</div>
						</Col>
					</Row>
				</div>

				{loading ? (
					<Loader className="fa-spin" />
				) : (
					<div className="documents-table-wrapper">
						<Table borderless className="documents-table">
							<thead>
								<tr>
									<th className="company-column">Company Name</th>
									<th className="type-column">Document Type</th>
									<th className="date-column">Date Added</th>
									<th className="description-column">Description</th>
									<th className="actions-column">Actions</th>
								</tr>
							</thead>
							<tbody>
								{filteredReports.map((report) => (
									<ReportRow key={report.id} report={report} />
								))}
							</tbody>
						</Table>
					</div>
				)}
			</div>

			<Modal isOpen={modal} centered className="document-upload-modal">
				<ModalHeader toggle={toggleAddForm}>
					<div className="d-flex align-items-center">
						<div className="upload-icon-wrapper mr-3">
							<Upload size={20} color="white" />
						</div>
						<span>Upload New Document</span>
					</div>
				</ModalHeader>
				<ModalBody className="px-4 py-4">
					<Form>
						<FormGroup className="mb-4">
							<Label className="font-weight-semibold small text-uppercase text-muted mb-2">
								Document Type
							</Label>
							<SelectField
								name="reportType"
								placeholder="Select Document Type"
								value={reportType}
								options={DOCUMENT_TYPES}
								onChange={(_name, val) => setReportType(val)}
								onBlur={() => {}}
								errors={{}}
								touched={{}}
							/>
						</FormGroup>

						<FormGroup className="mb-4">
							<Label className="font-weight-semibold small text-uppercase text-muted mb-2">
								Upload File
							</Label>
							<InputGroup className="file-upload-input">
								<Input
									type="file"
									accept={REPORTS_ALLOWED_FILE_EXTENSIONS.join(", ")}
									onChange={selectReportFile}
									className="d-none"
									id="file-input"
								/>
								<Label
									for="file-input"
									className="w-100 mb-0 py-3 d-flex flex-column align-items-center justify-content-center border-dashed rounded cursor-pointer"
									style={{ border: "2px dashed #cfdbea", backgroundColor: "#f8f9fa" }}
								>
									{reportFile ? (
										<div className="text-center px-3">
											<FileText size={32} color="#3f65f1" className="mb-2" />
											<div className="font-weight-medium text-dark truncate-text" style={{ maxWidth: "250px" }}>
												{reportFile.name}
											</div>
											<div className="small text-muted">
												{(reportFile.size / 1024).toFixed(2)} KB
											</div>
										</div>
									) : (
										<>
											<Upload size={32} color="#cfdbea" className="mb-2" />
											<span className="text-muted small">Drop files here or click to upload</span>
										</>
									)}
								</Label>
							</InputGroup>
							<p className={`small mt-2 mb-0 ${isFormatValid ? "text-success" : "text-danger"}`}>
								{errorMessage}
							</p>
						</FormGroup>

						<FormGroup className="mb-3">
							<Label className="font-weight-semibold small text-uppercase text-muted mb-2">
								Description
							</Label>
							<Input
								type="textarea"
								rows={4}
								value={reportDescription}
								onChange={(e) => setReportDescription(e.target.value)}
								name="description"
								placeholder="Provide a detailed description of the document..."
								className={`description-textarea ${
									reportDescription.length > 0 && !isDescriptionValid ? "is-invalid" : ""
								}`}
							/>
							<div className="d-flex justify-content-between mt-2">
								<span className={`small ${isDescriptionValid ? "text-success" : "text-muted"}`}>
									Minimum 50 characters required
								</span>
								<span className={`small ${isDescriptionValid ? "text-success" : "text-muted"}`}>
									{reportDescription.length} / 50
								</span>
							</div>
						</FormGroup>
					</Form>
				</ModalBody>
				<ModalFooter className="bg-light border-top-0 px-4 py-3">
					<Button outline onClick={toggleAddForm} className="px-4 border-0">
						Cancel
					</Button>
					<Button
						className="px-4"
						color="primary"
						onClick={createNewReport}
						disabled={!isFormatValid || adding || !reportFile || !isDescriptionValid || !reportType}
					>
						{adding ? (
							<>
								<Loader size={16} className="mr-2 fa-spin" />
								Uploading...
							</>
						) : (
							<>
								<Plus size={18} className="mr-2" />
								Upload Document
							</>
						)}
					</Button>
				</ModalFooter>
			</Modal>
		</div>
	);
};

const mapStateToProps = (state: IApplicationState) => {
	return {
		reports: state.report.reports.list,
		pagination: state.report.reports.pagination,
		requests: state.report.requests,
		user_email: state.user.profile?.email, // for determing display of sample report temporarily
		role: state.user.profile?.role
	};
};

const mapDispatchToProps = {
	getAllReportRequest: () => reportService.getAllReportRequests(),
	getAllReports: () => reportService.getAllReports(),
	paginateReports: (page: number) => (dispatch: Dispatch) => {
		dispatch(paginateReports(page));
	},
	addReport: (reportType: string, reportFile: File, reportDescription: string) =>
		reportService.addReport(reportType, reportFile, reportDescription),
	addReportRequest: (reportType: string, reportDescription: string) =>
		reportService.addReportRequest(reportType, reportDescription)
};

export default connect(mapStateToProps, mapDispatchToProps)(Labels);
